import { deburr, get } from 'lodash';

import { danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { downloadIndividualFile, waitUntilZipIsReadyAndGetPresignedURL } from 'utils/file';
import { getAccessParams } from 'utils/shared-access';
import { API, get as getRequest, patch } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import type { Action, ClientApprovalCampaign, ClientApprovalGlobalState, ThunkAction } from 'types';

applyTranslations(require('locales/en/messages.json'));
applyTranslations(require('../components/client-approval/client-approval.i18n.json'));

const RESET = 'clientApproval/RESET';
const DATA_LOADING = 'clientApproval/DATA_LOADING';
const DATA_LOADED = 'clientApproval/DATA_LOADED';
const CAMPAIGN_LOADED = 'clientApproval/CAMPAIGN_LOADED';
const INFLUENCERS_LOADED = 'clientApproval/INFLUENCERS_LOADED';
const INFLUENCERS_LOADING = 'clientApproval/INFLUENCERS_LOADING';
const INFLUENCERS_RESET = 'clientApproval/INFLUENCERS_RESET';
const BRAND_NOTE_CHANGED = 'clientApproval/BRAND_NOTE_CHANGED';
const APPROVAL_STATUS_CHANGED = 'clientApproval/APPROVAL_STATUS_CHANGED';

const initialState: ClientApprovalGlobalState = {
  isLoading: false,
  list: [],
  influencers: [],
  pagination: {},
  campaign: null
};

export function resetClientApproval(): Action {
  return {
    type: RESET
  };
}

export function resetInfluencers(): Action {
  return {
    type: INFLUENCERS_RESET
  };
}

function dataLoading(isLoading: boolean): Action {
  return {
    type: DATA_LOADING,
    payload: {
      isLoading
    }
  };
}

function dataLoaded(data: any): Action {
  return {
    type: DATA_LOADED,
    payload: {
      data
    }
  };
}

function campaignLoaded(campaign?: ClientApprovalCampaign | null): Action {
  return {
    type: CAMPAIGN_LOADED,
    payload: {
      campaign
    }
  };
}

function influencersLoading(isLoading: boolean): Action {
  return {
    type: INFLUENCERS_LOADING,
    payload: {
      isLoading
    }
  };
}

function influencersLoaded(data: any): Action {
  return {
    type: INFLUENCERS_LOADED,
    payload: {
      data
    }
  };
}

function brandNoteChanged(bucketId: number, note: string): Action {
  return {
    type: BRAND_NOTE_CHANGED,
    payload: {
      bucketId,
      note
    }
  };
}

function approvalStatusChanged(bucketId: number, itemId: number, approvalStatus: string): Action {
  return {
    type: APPROVAL_STATUS_CHANGED,
    payload: {
      bucketId,
      itemId,
      approvalStatus
    }
  };
}

export function fetchClientApproval(influencerId: number, page: number, sort: string): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const { config, options } = getAccessParams(getState);
      const url = `${API.END_POINTS.SHARED_ACCESS.fetchBuckets(String(influencerId || ''), page, sort)}`;

      dispatch(dataLoading(true));
      const response = await getRequest(url, config, options);
      dispatch(dataLoaded(response.data));
    } catch (error) {
      logError(error);
    } finally {
      dispatch(dataLoading(false));
    }
  };
}

export function fetchCampaign(): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.SHARED_ACCESS.fetchCampaign();

      const response = await getRequest(url, config, options);
      dispatch(campaignLoaded(response.data));
    } catch (error) {
      logError(error);
      dispatch(campaignLoaded(null));
    }
  };
}

export function fetchInfluencers(): ThunkAction {
  return async (dispatch, getState) => {
    try {
      dispatch(influencersLoading(true));

      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.SHARED_ACCESS.fetchCreators();
      const response = await getRequest(url, config, options);

      dispatch(influencersLoaded(response.data));
    } catch (error) {
      dispatch(influencersLoading(false));
      logError(error);
    }
  };
}

export function updateBrandNote(bucketId: number, note: string): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.SHARED_ACCESS.updateBucketNotes(bucketId);
      const payload = {
        notes: {
          brandRepresentative: note
        }
      };

      await patch(url, { headers: config.headers, data: payload }, options);
      dispatch(brandNoteChanged(bucketId, note));
      dispatch(success(__t('client_approval.note_save_success')));
    } catch (error) {
      logError(error);
      dispatch(danger(__t('unknown_error')));
    }
  };
}

export function updateApprovalStatus(bucketId: number, itemId: number, approvalStatus: string): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.SHARED_ACCESS.updateBucketItemApprovalStatus(bucketId, itemId);
      const payload = {
        approvalStatus
      };

      await patch(url, { headers: config.headers, data: payload }, options);
      dispatch(approvalStatusChanged(bucketId, itemId, approvalStatus));
      dispatch(fetchCampaign());
      dispatch(success(__t('client_approval.approval_status_saved')));
    } catch (error) {
      dispatch(danger(__t('client_approval.approval_status_error')));
      logError(error);
    }
  };
}

export function downloadCampaignContent(campaingName: string = 'campaign'): ThunkAction {
  const loaderId = 'downloadSharedCampaignContent()';

  return async (dispatch, getState) => {
    const url = API.END_POINTS.SHARED_ACCESS.downloadDeliverables(getState().sharedAccess.token, campaingName);
    dispatch(showLoading(loaderId));

    try {
      const { config, options } = getAccessParams(getState);
      const response = await getRequest(url, config, options);
      const zipFileS3Url = get(response, 'data.url', null);
      const jsonFileS3Url = get(response, 'data.statusUrl', null);
      if (zipFileS3Url && jsonFileS3Url) {
        // wait a bit before trying to download zip otherwise it's possible to get 403 from S3
        // probably because it's accessed too soon after file creation/update
        // eslint-disable-next-line no-promise-executor-return
        await new Promise(resolve => setTimeout(resolve, 1000));

        const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
        await downloadIndividualFile(presignedURL ?? zipFileS3Url);
      } else {
        throw new Error('Zippy returned an empty response');
      }
      dispatch(hideLoading(loaderId));
    } catch (error) {
      dispatch(danger(__t('client_approval.download_all_approved_content_error')));
      dispatch(hideLoading(loaderId));
    }
  };
}

export const downloadLook = (
  bucketId: number,
  lookName: string = 'look',
  campaignName: string = 'campaign'
): ThunkAction => {
  const loaderId = 'downloadLook()';
  const fileName = deburr(`collabary-${campaignName}-${lookName}`).replace(/\s/, '-').toLowerCase();

  return async (dispatch, getState) => {
    const token = getState().sharedAccess.token;
    const url = API.END_POINTS.SHARED_ACCESS.downloadLook(bucketId, token, fileName);

    dispatch(showLoading(loaderId));

    try {
      const { config, options } = getAccessParams(getState);
      const response = await getRequest(url, config, options);
      const zipFileS3Url = get(response, 'data.url', null);
      const jsonFileS3Url = get(response, 'data.statusUrl', null);

      if (zipFileS3Url && jsonFileS3Url) {
        // wait a bit before trying to download zip otherwise it's possible to get 403 from S3
        // probably because it's accessed too soon after file creation/update
        // eslint-disable-next-line no-promise-executor-return
        await new Promise(resolve => setTimeout(resolve, 1000));

        const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
        await downloadIndividualFile(presignedURL ?? zipFileS3Url);
      } else {
        throw new Error('Zippy returned an empty response');
      }

      dispatch(hideLoading(loaderId));
    } catch (error) {
      dispatch(danger(__t('client_approval.errors.download_approved_content_error')));
      dispatch(hideLoading(loaderId));
    }
  };
};

export default createReducer<ClientApprovalGlobalState, any>('clientApproval', initialState, {
  [RESET]: () => initialState,
  [DATA_LOADING]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [DATA_LOADED]: (state, { data }) => ({
    ...state,
    list: data.data.map(result => ({
      ...result,
      items: result.items.map(item => ({
        ...item,
        isSelected: item.isSelected || false
      }))
    })),
    pagination: {
      ...data.meta,
      links: data.links
    }
  }),
  [CAMPAIGN_LOADED]: (state, { campaign }) => ({
    ...state,
    campaign
  }),
  [INFLUENCERS_LOADING]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [INFLUENCERS_LOADED]: (state, { data }) => ({
    ...state,
    influencers: data
  }),
  [BRAND_NOTE_CHANGED]: (state, { bucketId, note }) => ({
    ...state,
    list: state.list.map(bucket =>
      (bucketId === bucket.id ?
        {
          ...bucket,
          notes: { ...bucket.notes, brandRepresentative: note }
        } :
        bucket
      ))
  }),
  [APPROVAL_STATUS_CHANGED]: (state, { bucketId, itemId, approvalStatus }) => ({
    ...state,
    list: state.list.map(bucket =>
      (bucketId === bucket.id ?
        {
          ...bucket,
          items: bucket.items.map(item => (item.id === itemId ? { ...item, approvalStatus } : item))
        } :
        bucket
      ))
  })
});
